import { useCallback, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { useAuth } from '@/context/AuthContext';

export function useWorkspaceState<Value>(
  key: string,
  initialValue: Value,
  validate?: (value: unknown) => boolean,
  storage: 'session' | 'local' = 'session'
): [Value, Dispatch<SetStateAction<Value>>] {
  const { user } = useAuth();
  const storageKey = `workspace:${user?.username || 'anonymous'}:${key}`;
  const defaults = useRef({ initialValue, validate });
  const readValue = useCallback((): Value => {
    try {
      const stored = (storage === 'local' ? localStorage : sessionStorage).getItem(storageKey);
      if (stored === null) return defaults.current.initialValue;
      const parsed: unknown = JSON.parse(stored);
      const isValid = defaults.current.validate
        ? defaults.current.validate(parsed)
        : typeof parsed === typeof defaults.current.initialValue && parsed !== null;
      return isValid ? (parsed as Value) : defaults.current.initialValue;
    } catch {
      return defaults.current.initialValue;
    }
  }, [storageKey, storage]);
  const [state, setState] = useState(() => ({ key: storageKey, value: readValue() }));
  const value = state.key === storageKey ? state.value : readValue();
  const setValue = useCallback<Dispatch<SetStateAction<Value>>>(
    update => {
      setState(previous => {
        const current = previous.key === storageKey ? previous.value : readValue();
        const next =
          typeof update === 'function' ? (update as (previous: Value) => Value)(current) : update;
        try {
          (storage === 'local' ? localStorage : sessionStorage).setItem(
            storageKey,
            JSON.stringify(next)
          );
        } catch {}
        return { key: storageKey, value: next };
      });
    },
    [storageKey, storage, readValue]
  );
  return [value, setValue];
}
